// Plain-English translations for the rule DSL.
//
// Shared by the rules wiki (`/rules`) and the per-finding detail panel on
// the audit-run page. When the wiki adds a new predicate / formula op,
// extend these helpers so both surfaces pick it up.

import type {
  NestedTrigger,
  RulePredicate,
  RuleRecoveryFormula,
  RuleTriggerNode,
} from '@/api/types';

/** A trigger node is a predicate if it carries a `field`; otherwise it's
 *  a nested boolean combinator. */
export function isPredicate(node: RuleTriggerNode): node is RulePredicate {
  return typeof (node as RulePredicate).field === 'string';
}

/** Human-readable rendering of a single trigger predicate.
 *
 *  The wire ships every comparator slot — set ones carry the value,
 *  unset ones carry `null` — so every branch tests with `!= null`
 *  (skips both null and undefined) rather than `'in'` checks that
 *  would always be true. Only `equals` allows `0` as a valid value;
 *  the `equals` branch uses an explicit non-null check so `equals: 0`
 *  isn't filtered out. */
export function describePredicate(p: RulePredicate): string {
  const fieldLabel = humanField(p.field);
  if (p.equals !== undefined && p.equals !== null) {
    return `${capitalize(fieldLabel)} equals ${formatValue(p.equals)}.`;
  }
  if (p.not_equals !== undefined && p.not_equals !== null) {
    return `${capitalize(fieldLabel)} is not ${formatValue(p.not_equals)}.`;
  }
  if (p.one_of != null) {
    return `${capitalize(fieldLabel)} is one of: ${p.one_of.map((v) => formatValue(v)).join(', ')}.`;
  }
  if (p.contains_any != null) {
    return `${capitalize(fieldLabel)} contains any of: ${p.contains_any.map((v) => formatValue(v)).join(', ')}.`;
  }
  if (p.gte != null) {
    return `${capitalize(fieldLabel)} is at least ${formatValue(p.gte)}.`;
  }
  if (p.lte != null) {
    return `${capitalize(fieldLabel)} is at most ${formatValue(p.lte)}.`;
  }
  if (p.within_engagement_period === true) {
    return `${capitalize(fieldLabel)} falls inside the engagement period.`;
  }
  if (p.within_reclaim_window_years != null) {
    const yrs = p.within_reclaim_window_years;
    return `${capitalize(fieldLabel)} is within the last ${yrs} year${yrs === 1 ? '' : 's'} (CRA reclaim window).`;
  }
  if (p.within_tolerance_of != null) {
    return `${capitalize(fieldLabel)} is within ${describeTolerance(p.within_tolerance_of)} of ${formatValue(p.within_tolerance_of.expected)}.`;
  }
  // Fallthrough: source predicate was `equals: null` — the unknown-
  // province fallback rules use this shape. INP-360.
  return `${capitalize(fieldLabel)} is empty (not recorded).`;
}

function describeTolerance(t: NonNullable<RulePredicate['within_tolerance_of']>): string {
  if (t.tolerance_pct != null) {
    const pct = Number(t.tolerance_pct) * 100;
    return `${pct.toFixed(2).replace(/\.?0+$/, '')}%`;
  }
  if (t.tolerance_dollars != null) return formatValue(t.tolerance_dollars);
  return '?';
}

/** Compact symbolic rendering of a predicate — for monospace tree
 *  display. Returns `[field, op, value]` triples so the renderer can
 *  align columns. */
export function predicateTriple(p: RulePredicate): {
  field: string;
  op: string;
  value: string;
} {
  if (p.equals !== undefined && p.equals !== null) {
    return { field: p.field, op: '=', value: formatValueCompact(p.equals) };
  }
  if (p.not_equals !== undefined && p.not_equals !== null) {
    return { field: p.field, op: '≠', value: formatValueCompact(p.not_equals) };
  }
  if (p.one_of != null) {
    return {
      field: p.field,
      op: 'in',
      value: `[${p.one_of.map(formatValueCompact).join(', ')}]`,
    };
  }
  if (p.contains_any != null) {
    return {
      field: p.field,
      op: 'contains',
      value: `[${p.contains_any.map(formatValueCompact).join(', ')}]`,
    };
  }
  if (p.gte != null) return { field: p.field, op: '≥', value: formatValueCompact(p.gte) };
  if (p.lte != null) return { field: p.field, op: '≤', value: formatValueCompact(p.lte) };
  if (p.within_engagement_period === true) {
    return { field: p.field, op: 'in', value: 'engagement period' };
  }
  if (p.within_reclaim_window_years != null) {
    return {
      field: p.field,
      op: 'within',
      value: `${p.within_reclaim_window_years}y reclaim window`,
    };
  }
  if (p.within_tolerance_of != null) {
    const { expected, tolerance_pct, tolerance_dollars } = p.within_tolerance_of;
    const tol =
      tolerance_pct != null
        ? `±${(Number(tolerance_pct) * 100).toFixed(2).replace(/\.?0+$/, '')}%`
        : tolerance_dollars != null
          ? `±${formatValueCompact(tolerance_dollars)}`
          : '±?';
    return { field: p.field, op: '≈', value: `${formatValueCompact(expected)} ${tol}` };
  }
  // Fallthrough: every comparator on the wire is null, which means the
  // source predicate was literally `equals: null` (every other comparator
  // would render above with its own branch). INP-360's unknown-province
  // fallback rules use this shape — render as the "field is null" check
  // they actually express, not as `(unknown)`.
  return { field: p.field, op: 'is', value: 'null' };
}

/** Which combinator a nested trigger carries — caller renders the label
 *  (`ALL`, `ANY`, `NOT`) above the children. The wire ships every slot
 *  (with `null` in the unset ones) so we use `!= null` rather than
 *  truthy checks; the chosen slot is the only non-null one. */
export function combinatorOf(t: NestedTrigger): {
  kind: 'all' | 'any' | 'not';
  children: RuleTriggerNode[];
} {
  if (t.all_of != null) return { kind: 'all', children: t.all_of };
  if (t.any_of != null) return { kind: 'any', children: t.any_of };
  if (t.not_ != null) return { kind: 'not', children: [t.not_] };
  return { kind: 'all', children: [] };
}

/** Human-readable rendering of a recovery formula (with no concrete numbers). */
export function describeFormula(f: RuleRecoveryFormula): string {
  if (f.op === 'factor') {
    const baseLabel = humanField(f.base_field);
    if (f.numerator === 0) {
      return `Review-only — no recovery $ claimed.`;
    }
    if (f.numerator === 1 && f.denominator === 1) {
      return `Recovery equals the full ${baseLabel.toLowerCase()}.`;
    }
    return `Recovery equals ${f.numerator}/${f.denominator} of the ${baseLabel.toLowerCase()} (the Factor Method).`;
  }
  if (f.op === 'rate_lookup_factor') {
    const baseLabel = humanField(f.base_field).toLowerCase();
    const asOfLabel = humanField(f.as_of_field).toLowerCase();
    return `Recovery extracts the embedded ${f.rate_type.toUpperCase()} from the ${baseLabel} at the rate in force on the ${asOfLabel} (date-aware Factor Method).`;
  }
  return 'Recovery formula uses an operation the wiki doesn’t yet translate.';
}

/** Dotted-path field name → display label. */
export function humanField(field: string): string {
  const dict: Record<string, string> = {
    id: 'Transaction ID',
    bill_date: 'Bill date',
    bill_amount: 'Bill amount',
    tax_amount: 'Tax amount',
    tax_code: 'Tax code',
    transaction_type: 'Transaction type',
    transaction_category: 'Expense category',
    meal_service_type: 'Meal service type',
    implied_tax_rate: 'Effective tax rate (tax ÷ bill)',
    'supplier.name': 'Supplier name',
    'supplier.bn': 'Supplier Business Number (BN)',
    'supplier.gst_registrant': 'Supplier is GST/HST registered',
    'supplier.province': 'Supplier province (from curated map)',
    'supplier.tax_class': 'Supplier tax class',
    'gl_account.name': 'GL account name',
  };
  if (dict[field]) return dict[field];
  return field
    .replace(/\./g, ' — ')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

/** Format a primitive JSON value as display text. */
export function formatValue(v: unknown): string {
  if (v === null) return 'empty';
  if (typeof v === 'string') return v === '' ? '(blank)' : `"${v}"`;
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
}

/** Same as `formatValue` but tuned for the monospace tree — strings
 *  keep their quotes for legibility but numbers/booleans render bare.
 *  Pydantic serializes Decimal as a JSON string ("0.13", "100"); those
 *  pass an isFinite parse and render unquoted so the tree reads like
 *  arithmetic rather than tagged-value pairs. */
export function formatValueCompact(v: unknown): string {
  if (v === null) return 'null';
  if (typeof v === 'string') {
    if (v === '') return '""';
    // Numeric Decimal strings come over the wire from Pydantic; render
    // them as bare numbers. Plain alphanumeric strings ("ON", "Bill")
    // stay quoted so the reader sees the literal.
    if (v.trim() !== '' && Number.isFinite(Number(v))) return v;
    return `"${v}"`;
  }
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return String(v);
  return String(v);
}

export function capitalize(s: string): string {
  return s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s;
}
